import { Row } from '@tanstack/react-table';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { toTitleCase } from '@/react/common/string-utils';

import { filterHOC } from '@@/datatables/Filter';

import { TRIVY_SEVERITIES, TrivySeverity } from '../../types';
import { severityBadgeType } from '../../severityDisplay';
import { VulnerabilityRow } from '../types';

import { columnHelper } from './helper';
import { CountBadge } from './CountBadge';

// Filtering lives here, not on highest-severity, because it matches these
// counts: filtering for High wants every workload carrying one.
export const counts = columnHelper.accessor('counts', {
  header: 'Findings',
  id: 'counts',
  cell: ({ row: { original: row } }) => <FindingBadges row={row} />,
  // Accessor, not display: getCanFilter requires one, and the header only
  // renders a filter for a filterable column.
  enableSorting: false,
  meta: {
    filter: filterHOC<VulnerabilityRow>('Filter by severity', severityOptions),
  },
  enableColumnFilter: true,
  filterFn: (
    row: Row<VulnerabilityRow>,
    _columnId: string,
    filterValue: Array<string>
  ) =>
    filterValue.length === 0 ||
    filterValue.some((label) => {
      const severityValue = toSeverity(label);
      return !!severityValue && (row.original.counts[severityValue] ?? 0) > 0;
    }),
});

function FindingBadges({ row }: { row: VulnerabilityRow }) {
  const environmentId = useEnvironmentId();

  return (
    <div className="flex flex-wrap gap-1">
      {TRIVY_SEVERITIES.map((severity) => (
        <CountBadge
          key={severity}
          environmentId={environmentId}
          namespace={row.namespace}
          workload={row.workload}
          severity={severity}
          type={severityBadgeType(severity)}
          label={toTitleCase(severity)}
          count={row.counts[severity]}
        />
      ))}
    </div>
  );
}

// Faceted options would offer only each row's worst severity, hiding the rest.
function severityOptions(rows: Array<Row<VulnerabilityRow>>): Array<string> {
  return TRIVY_SEVERITIES.filter((severityValue) =>
    rows.some((row) => (row.original.counts[severityValue] ?? 0) > 0)
  ).map(toTitleCase);
}

function toSeverity(label: string): TrivySeverity | undefined {
  return TRIVY_SEVERITIES.find(
    (severityValue) => toTitleCase(severityValue) === label
  );
}
